import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { remCalc } from '@/styles/typography/utils';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  BREAKPOINT_PADDING_VAR,
  BREAKPOINT_VAR,
  mediaBreakpointOnly,
  mediaBreakpointUp,
} from '@/styles/layout/utils';
import { COLORS } from '@/styles/color';

export interface TextColumnsProps extends PropsWithChildren, PropsWithClassName {
  imageSize?: 'small' | 'large';
}

const TextColumns: FC<TextColumnsProps> = ({ imageSize = 'small', className, children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const observer = useRef<ResizeObserver | null>(null);

  const [thumbWidth, setThumbWidth] = useState(20);
  const [scrollStartPosition, setScrollStartPosition] = useState<number>(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  const hideScrollbar = useMemo(() => {
    if (!scrollTrackRef.current) return false;

    return thumbWidth >= scrollTrackRef.current.clientWidth;
  }, [thumbWidth, scrollTrackRef]);

  const handleTrackClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const { current: trackCurrent } = scrollTrackRef;
      const { current: contentCurrent } = contentRef;

      if (trackCurrent && contentCurrent) {
        const { clientX } = e;
        const target = e.target as HTMLDivElement;
        const rect = target.getBoundingClientRect();
        const trackLeft = rect.left;
        const thumbOffset = -(thumbWidth / 2);
        const clickRatio = (clientX - trackLeft + thumbOffset) / trackCurrent.clientWidth;
        const scrollAmount = Math.floor(clickRatio * contentCurrent.scrollWidth);

        contentCurrent.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    },
    [thumbWidth],
  );

  const handleThumbPosition = useCallback(() => {
    if (!contentRef.current || !scrollTrackRef.current || !scrollThumbRef.current) return;

    const { scrollLeft: contentLeft, scrollWidth: contentWidth } = contentRef.current;
    const { clientWidth: trackWidth } = scrollTrackRef.current;

    let newLeft = (+contentLeft / +contentWidth) * trackWidth;

    newLeft = Math.floor(Math.min(newLeft, trackWidth - thumbWidth));

    scrollThumbRef.current.style.left = `${newLeft}px`;
  }, []);

  const handleThumbMousedown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setScrollStartPosition(e.clientX);

    if (contentRef.current) setInitialScrollLeft(contentRef.current.scrollLeft);

    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isDragging) setIsDragging(false);
    },
    [isDragging],
  );

  const handleThumbMousemove = useCallback(
    (e) => {
      if (isDragging && contentRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const { scrollWidth: contentScrollWidth, offsetWidth: contentOffsetWidth } = contentRef.current;

        const deltaX = (e.clientX - scrollStartPosition) * (contentOffsetWidth / thumbWidth);
        const newScrollLeft = Math.min(initialScrollLeft + deltaX, contentScrollWidth - contentOffsetWidth);

        contentRef.current.scrollLeft = newScrollLeft;
      }
    },
    [isDragging, scrollStartPosition, thumbWidth],
  );

  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const ref = contentRef.current;

      observer.current = new ResizeObserver(() => {
        const { clientWidth, scrollWidth } = ref;
        const { clientWidth: trackWidth } = scrollTrackRef.current!;

        setThumbWidth((clientWidth / scrollWidth) * trackWidth);
      });

      observer.current.observe(ref);

      ref.addEventListener('scroll', handleThumbPosition);

      return () => {
        observer.current?.unobserve(ref);
        ref.removeEventListener('scroll', handleThumbPosition);
      };
    }
  }, [contentRef.current, scrollTrackRef.current]);

  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);

    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);

  return (
    <Wrapper className={className}>
      <ContentWrapper ref={contentRef} imageSize={imageSize}>
        {children}
      </ContentWrapper>

      <Scrollbar hide={hideScrollbar}>
        <ScrollbarTrackWrapper>
          <ScrollbarTrack
            ref={scrollTrackRef}
            onClick={handleTrackClick}
            style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
          />

          <ScrollbarThumb
            ref={scrollThumbRef}
            onTouchStart={handleThumbMousedown}
            onMouseDown={handleThumbMousedown}
            style={{
              width: `${thumbWidth}px`,
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          />
        </ScrollbarTrackWrapper>
      </Scrollbar>
    </Wrapper>
  );
};

export default TextColumns;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div<{ imageSize: TextColumnsProps['imageSize'] }>`
  --gap: var(--text-columns-gap, 20px);
  --column-count: var(--text-columns-count, 3);

  ${(props) => {
    if (props.imageSize === 'small') {
      return css`
        --image-size: var(--text-columns-image-size, 50px);

        ${mediaBreakpointUp('tablet')} {
          --image-size: var(--text-columns-image-size, 80px);
        }
      `;
    }

    if (props.imageSize === 'large') {
      return css`
        --image-size: var(--text-columns-image-size, 100px);

        ${mediaBreakpointUp('tablet')} {
          --image-size: var(--text-columns-image-size, 160px);
        }
      `;
    }
  }}

  display: flex;
  flex-direction: column;
  gap: var(--gap);

  ${mediaBreakpointOnly('tablet')} {
    ${applyBreakpointNegativeMargins}
    ${applyBreakpointPadding}
  }

  ${mediaBreakpointUp('tablet')} {
    ${applyBreakpointNegativeMargins}
    ${applyBreakpointPadding}

    flex-direction: row;

    overflow: auto;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      flex-basis: calc((${BREAKPOINT_VAR} - (${BREAKPOINT_PADDING_VAR} * 2) - var(--gap)) / 2);
    }
  }

  ${mediaBreakpointUp('desktop')} {
    flex-wrap: wrap;
    justify-content: center;

    > * {
      // prettier-ignore
      max-width: calc((100% - (var(--gap) * (var(--column-count) - 1))) / var(--column-count));
    }
  }
`;

const Scrollbar = styled.div<{ hide: boolean }>`
  display: none;
  margin-top: ${remCalc(30)};
  width: 100%;

  ${mediaBreakpointOnly('tablet')} {
    display: inline-block;

    ${(props) =>
      props.hide
        ? css`
            margin-top: 0;

            &,
            * {
              opacity: 0 !important;
              pointer-events: none !important;
              visibility: hidden !important;
              height: 0 !important;
            }
          `
        : css``}
  }
`;

const ScrollbarTrackWrapper = styled.div`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  display: block;
  height: 16px;
  width: 100%;
  border-radius: 100px;
  position: relative;
`;

const ScrollbarTrack = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  position: relative;
`;

const ScrollbarThumb = styled.div`
  border-radius: 100px;
  background: ${COLORS.BLACK};
  position: absolute;
  height: 100%;
  min-width: 60px;
  top: 0;
  border: 3px solid ${COLORS.GRAY.HIGHLIGHT};
`;
